import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { PRODUCT_STATUSES } from '@solar/contracts';

import { Alert } from '../../../components/Alert';
import { Button } from '../../../components/Button';
import { FormField } from '../../../components/forms/FormField';
import { PageSpinner } from '../../../components/PageSpinner';
import { useSuccessNotification } from '../../../components/SuccessNotification';
import { catalogueApi } from '../../../features/catalogue/catalogue.api';
import { toDisplayMessage } from '../../../lib/error-message';
import { formatStatusLabel } from '../../../lib/format-status';
import { routeTo } from '../../../routes/paths';

import styles from './AdminPortal.module.css';

export function AdminProductFormPage(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const isEdit = id !== undefined && id !== 'new';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { notify, dismiss } = useSuccessNotification();

  const product = useQuery({
    queryKey: ['admin', 'products', id],
    queryFn: () => catalogueApi.getAdminProduct(id ?? ''),
    enabled: isEdit,
  });

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('RESIDENTIAL');
  const [systemCapacityKw, setSystemCapacityKw] = useState('5.0');
  const [basePrice, setBasePrice] = useState('');
  const [warrantySummary, setWarrantySummary] = useState('');
  const [status, setStatus] = useState<string>(PRODUCT_STATUSES[0]);
  const [isAvailable, setIsAvailable] = useState<'true' | 'false'>('true');
  const [componentType, setComponentType] = useState('PANEL');
  const [componentName, setComponentName] = useState('');
  const [componentQuantity, setComponentQuantity] = useState('1');
  const [componentUnit, setComponentUnit] = useState('unit');

  useEffect(() => {
    if (product.data !== undefined) {
      setCode(product.data.code);
      setName(product.data.name);
      setSlug(product.data.slug);
      setDescription(product.data.description);
      setCategory(product.data.category);
      setSystemCapacityKw(product.data.systemCapacityKw);
      setBasePrice(product.data.basePrice);
      setWarrantySummary(product.data.warrantySummary ?? '');
      setStatus(product.data.status);
      setIsAvailable(product.data.isAvailable === 'true' ? 'true' : 'false');
    }
  }, [product.data]);

  const save = useMutation({
    mutationFn: () => {
      if (isEdit) {
        return catalogueApi.updateProduct(id ?? '', {
          name,
          description,
          category,
          systemCapacityKw,
          basePrice,
          warrantySummary: warrantySummary === '' ? undefined : warrantySummary,
          status,
          isAvailable,
        });
      }

      return catalogueApi.createProduct({
        code,
        name,
        slug,
        description,
        category,
        systemCapacityKw,
        basePrice,
        warrantySummary: warrantySummary === '' ? undefined : warrantySummary,
        isAvailable,
      });
    },
    onMutate: () => {
      dismiss();
    },
    onSuccess: (saved) => {
      notify(isEdit ? 'Product updated.' : 'Product created.');
      void queryClient.invalidateQueries({ queryKey: ['admin', 'products'] });
      void navigate(routeTo.adminProduct(saved.id));
    },
  });

  const addComponent = useMutation({
    mutationFn: () =>
      catalogueApi.addProductComponent(id ?? '', {
        componentType,
        componentName,
        quantity: Number.parseInt(componentQuantity, 10),
        unit: componentUnit,
      }),
    onMutate: () => {
      dismiss();
    },
    onSuccess: async () => {
      notify('Product component added.');
      setComponentName('');
      setComponentQuantity('1');
      await queryClient.invalidateQueries({ queryKey: ['admin', 'products', id] });
    },
  });

  if (isEdit && product.isPending) {
    return <PageSpinner label="Loading product" />;
  }

  if (isEdit && product.isError) {
    return (
      <Alert tone="error">
        {toDisplayMessage(product.error)}{' '}
        <Button variant="secondary" onClick={() => void product.refetch()}>
          Retry
        </Button>
      </Alert>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div>
          <p className={styles.eyebrow}>Catalogue management</p>
          <h1 className={styles.pageTitle}>{isEdit ? 'Edit product' : 'Create product'}</h1>
          <p className={styles.pageSubtitle}>
            Product identity, pricing, customer availability and included equipment.
          </p>
        </div>
      </header>

      <section className={styles.card} aria-labelledby="product-form-heading">
        <h2 className={styles.cardTitle} id="product-form-heading">
          Details
        </h2>

        {save.isError && <Alert tone="error">{toDisplayMessage(save.error)}</Alert>}

        <form
          className={styles.filters}
          onSubmit={(event) => {
            event.preventDefault();
            save.mutate();
          }}
        >
          {!isEdit && (
            <>
              <FormField
                label="Code"
                name="code"
                value={code}
                required
                onChange={(e) => setCode(e.target.value)}
              />
              <FormField
                label="Slug"
                name="slug"
                value={slug}
                required
                onChange={(e) => setSlug(e.target.value)}
              />
            </>
          )}
          <FormField
            label="Name"
            name="name"
            value={name}
            required
            onChange={(e) => setName(e.target.value)}
          />
          <FormField
            label="Description"
            name="description"
            value={description}
            required
            onChange={(e) => setDescription(e.target.value)}
          />
          <FormField
            label="Category"
            name="category"
            value={category}
            required
            onChange={(e) => setCategory(e.target.value)}
          />
          <FormField
            label="System capacity (kW)"
            name="systemCapacityKw"
            value={systemCapacityKw}
            required
            onChange={(e) => setSystemCapacityKw(e.target.value)}
          />
          <FormField
            label="Base price"
            name="basePrice"
            value={basePrice}
            required
            onChange={(e) => setBasePrice(e.target.value)}
          />
          <FormField
            label="Warranty summary"
            name="warrantySummary"
            value={warrantySummary}
            onChange={(e) => setWarrantySummary(e.target.value)}
          />
          {isEdit && (
            <label className={styles.filterLabel} htmlFor="product-edit-status">
              Status
              <select
                id="product-edit-status"
                value={status}
                onChange={(event) => setStatus(event.target.value)}
              >
                {PRODUCT_STATUSES.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className={styles.filterLabel} htmlFor="product-availability">
            Customer availability
            <select
              id="product-availability"
              value={isAvailable}
              onChange={(event) => setIsAvailable(event.target.value as 'true' | 'false')}
            >
              <option value="true">Available to customers</option>
              <option value="false">Hidden from customers</option>
            </select>
          </label>
          <Button type="submit" disabled={save.isPending}>
            Save product
          </Button>
        </form>
      </section>

      {isEdit && product.isSuccess && (
        <section className={styles.card} aria-labelledby="product-components-heading">
          <h2 className={styles.cardTitle} id="product-components-heading">
            Included components
          </h2>
          {product.data.components === undefined || product.data.components.length === 0 ? (
            <p className={styles.empty}>No components are recorded for this product.</p>
          ) : (
            <div className={styles.tableWrapper}>
              <table className={styles.table} aria-label="Product components">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Component</th>
                    <th>Quantity</th>
                    <th>Warranty</th>
                  </tr>
                </thead>
                <tbody>
                  {product.data.components.map((component) => (
                    <tr key={component.id}>
                      <td>{formatStatusLabel(component.componentType)}</td>
                      <td>{component.componentName}</td>
                      <td>
                        {component.quantity} {component.unit}
                      </td>
                      <td>
                        {component.warrantyMonths === null
                          ? '—'
                          : `${component.warrantyMonths} months`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <h3 className={styles.sectionTitle}>Add component</h3>
          {addComponent.isError && (
            <Alert tone="error">{toDisplayMessage(addComponent.error)}</Alert>
          )}
          <form
            className={styles.filters}
            onSubmit={(event) => {
              event.preventDefault();
              addComponent.mutate();
            }}
          >
            <label className={styles.filterLabel} htmlFor="component-type">
              Type
              <select
                id="component-type"
                value={componentType}
                onChange={(event) => setComponentType(event.target.value)}
              >
                {[
                  'PANEL',
                  'INVERTER',
                  'BATTERY',
                  'CHARGE_CONTROLLER',
                  'CABLING',
                  'MOUNTING',
                  'INSTALLATION',
                  'ACCESSORY',
                  'OTHER',
                ].map((option) => (
                  <option key={option} value={option}>
                    {formatStatusLabel(option)}
                  </option>
                ))}
              </select>
            </label>
            <FormField
              label="Component name"
              name="componentName"
              value={componentName}
              required
              onChange={(event) => setComponentName(event.target.value)}
            />
            <FormField
              label="Quantity"
              name="componentQuantity"
              type="number"
              min="1"
              value={componentQuantity}
              required
              onChange={(event) => setComponentQuantity(event.target.value)}
            />
            <FormField
              label="Unit"
              name="componentUnit"
              value={componentUnit}
              required
              onChange={(event) => setComponentUnit(event.target.value)}
            />
            <Button type="submit" busy={addComponent.isPending}>
              Add component
            </Button>
          </form>
        </section>
      )}
    </div>
  );
}
